import React, {useMemo} from 'react'

import type {PublicUser} from '../types/public-user'

interface CurrentUserContextType {
  currentUser: PublicUser | undefined
  currentUserIsSessionOwner: boolean
  optedInToFeedback: boolean
}

const CurrentUserContext = React.createContext<CurrentUserContextType | undefined>(undefined)

interface CurrentUserContextProviderProps {
  children: React.ReactNode
  currentUser: PublicUser | undefined
  currentUserIsSessionOwner: boolean
  optedInToFeedback?: boolean
}

export function CurrentUserProvider({
  currentUser,
  currentUserIsSessionOwner,
  optedInToFeedback = false,
  children,
}: CurrentUserContextProviderProps) {
  const value = useMemo(
    () => ({currentUser, currentUserIsSessionOwner, optedInToFeedback}),
    [currentUser, currentUserIsSessionOwner, optedInToFeedback],
  )
  return <CurrentUserContext value={value}> {children} </CurrentUserContext>
}

export function useCurrentUser() {
  const context = React.use(CurrentUserContext)
  if (!context) {
    throw new Error('useCurrentUser must be used within a CurrentUserProvider')
  }
  return context
}

/**
 * Like useCurrentUser, but returns undefined instead of throwing when no
 * CurrentUserProvider is present. Useful for components that can render both
 * inside and outside the provider (e.g. AgentsList in the Copilot immersive).
 */
export function useCurrentUserOptional() {
  return React.use(CurrentUserContext)
}
